import type zhCN from './zh-CN';

// Typed against the zh-CN catalog so a missing English key is a compile error.
const en: Record<keyof typeof zhCN, string> = {
  'app.name': 'AssetList',
  'crash.title': 'Something went wrong',
  'crash.lead': 'This page hit an error while rendering. Your data is in this browser and is not lost. Below is the error; copy it to report it, or go home or reload to carry on.',
  'crash.copy': 'Copy error',
  'crash.home': 'Home',
  'crash.reload': 'Reload',
  'settings.crashes': 'Recent errors (kept in this browser, last 10)',
  'settings.crashesNone': 'No errors recorded.',
  'settings.crashesCopy': 'Copy all',
  'settings.crashesClear': 'Clear',
  'about.title': 'About AssetList',
  'about.footNote': '© 2026 Code Future Canada Inc. If the Chinese and English versions of this page differ, the English text prevails.',
  'footer.about': 'About AssetList',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms of Use',
  'dev.banner': 'This is the development build and may be unstable. If something breaks, reload the page — your data lives in this browser and is not lost. For stable use, go to',

  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.back': '← Back',
  'common.continue': 'Continue →',
  'common.skip': 'Skip for now →',
  'common.none': '—',
  'common.loading': 'Loading…',
  'common.generate': 'Generate',
  'common.regenerate': 'Regenerate',
  'common.generating': 'Generating…',
  'common.today': 'today',
  'common.send': 'Send',
  'common.retry': 'Retry',
  'common.stop': 'Stop',
  'common.unknownError': 'Unknown error',

  'header.subtitle': 'Personal asset ledger · data stays in this browser',
  'header.updated': 'Updated {time}',
  'header.refreshing': ' · refreshing',
  'header.currency': 'Display currency',
  'header.export': 'Export backup',
  'header.settings': 'Settings',
  'header.list': 'Assets',

  'class.stock': 'US stock',
  'class.etf': 'ETF',
  'class.cash': 'Cash',
  'class.crypto': 'Crypto',

  'fresh.realtime': 'Real-time',
  'fresh.delayed15': '15-min delayed',
  'fresh.close': 'Close',
  'fresh.rate': 'Daily rate',
  'fresh.manual': 'Manual price',
  'fresh.nav': 'NAV {date}',
  'fresh.navUndated': 'NAV',
  'fresh.stale': '{label} · stale',
  'fresh.none': 'No price',
  'fresh.fx': 'FX ECB {date}',
  'fresh.fxNone': 'FX unavailable',
  'fresh.base': 'Base',
  'source.manual': 'Manual',
  'source.ecb': 'ECB',

  'list.total': 'Total assets',
  'list.dayChange': '{amount} · {pct} today',
  'list.missing': '{n, plural, one {# unpriced} other {# unpriced}}',
  'list.col.asset': 'Asset',
  'list.col.qty': 'Qty',
  'list.col.cost': 'Cost',
  'list.col.price': 'Price',
  'list.col.value': 'Value · {ccy}',
  'list.col.day': 'Today',
  'list.col.data': 'Data',
  'list.items': '{n, plural, one {# item} other {# items}}',
  'list.itemsShare': '{n, plural, one {# item} other {# items}} · {pct}',
  'list.addAsset': 'Add asset',
  'list.deletePortfolio': 'Delete portfolio',
  'list.renameHint': 'Click to rename',
  'list.emptyPortfolio': 'This portfolio is still empty.',
  'list.newPortfolio': 'New portfolio',
  'list.portfolioName': 'Portfolio name',
  'list.create': 'Create',
  'list.confirmDeletePortfolio': 'Delete the portfolio “{name}”{n, plural, =0 {} one { and its # asset} other { and its # assets}}? This cannot be undone.',
  'list.footerNote': 'Holdings, costs and keys are stored only in this browser. Clearing site data erases everything — export a backup regularly.',
  'list.experts': 'Ask the experts',
  'list.emptyTitle': 'Start with your first portfolio',
  'list.emptyBody': 'A portfolio is one account or one kind of asset — “IBKR”, “Binance”, “Cash”. Everything stays in this browser.',
  'list.emptyNoKey': 'No market-data key yet: quotes come from the built-in source, about 15 minutes delayed; US listings are added by exact ticker.',
  'list.goSetup': 'About the sources',
  'list.cashSub': '{amount} {ccy}',
  'list.cashRate': ' · rate {rate}',
  'list.cashName': '{ccy} cash',

  'add.title': 'Add asset',
  'add.editTitle': 'Edit asset',
  'add.to': 'to {name}',
  'add.type': 'Type',
  'add.symbol': 'Symbol',
  'add.what': 'What do you hold',
  'add.whatPlaceholder': 'Type a code or a name: AAPL, SHOP, FCUV, FID5982, BTC…',
  'add.whatHint': 'US and Canadian listings by code or name; Canadian mutual funds by FundSERV code (e.g. FID5982). The kind and the market come from the code.',
  'add.whatHintNoUsKey': 'No market-data key: enter the exact US ticker (e.g. AAPL, BRK.B); Canadian stocks and mutual funds can be searched by name or code. Searching US listings by company name needs a Finnhub or Massive key.',
  'add.quick': 'Or add directly:',
  'add.noResults': 'Nothing found for {code}.',
  'add.manualToggle': 'Enter by hand',
  'add.backToSearch': 'Back to search',
  'add.change': 'Change',
  'add.navShort': 'NAV {nav} · {date}',
  'add.searching': 'Searching…',
  'add.coinNote': 'Prices from Coinbase public data, no key needed. More coins later.',
  'add.amount': 'Amount',
  'add.quantity': 'Quantity',
  'add.cost': 'Cost per unit · {ccy} (optional)',
  'add.costPlaceholder': 'per unit',
  'add.date': 'Purchase date (optional)',
  'add.manualPrice': 'Current price · {ccy} (manual)',
  'add.manualPlaceholder': 'Entered by hand when no source knows the code',
  'add.note': 'Note (optional)',
  'add.notePlaceholder': 'e.g. bought in two lots; no adds before earnings',
  'add.previewPrice': 'At the current price of {price}',
  'add.previewRate': 'At the current rate of {rate}',
  'add.previewNone': 'No price yet — fetched after adding',
  'add.value': 'Value',
  'add.footNote': 'Cost is recorded in the purchase currency and converted at the day’s rate for display.',
  'add.submit': 'Add asset',

  'detail.openGoogle': 'Open in Google Finance',
  'detail.openKoyfin': 'Open in Koyfin',
  'detail.koyfinTitle': 'Koyfin’s company overview (needs a Koyfin account; the free plan is enough); financials, valuation and charts are one click from there.',
  'detail.editHolding': 'Edit holding',
  'detail.ask': 'Ask the experts',
  'detail.confirmDelete': 'Delete “{name}”?',
  'detail.held': 'Held',
  'detail.shares': 'shares',
  'detail.avgCost': 'Avg. cost',
  'detail.value': 'Value · {ccy}',
  'detail.share': 'Of total',
  'detail.pnl': 'vs cost',
  'detail.purchased': 'Purchased',
  'detail.cashNote': 'Cash is converted at the ECB reference rate, updated once per working day at 16:00 CET; there is no chart.',
  'detail.note': 'Note: {note}',
  'detail.chartNote': 'TradingView chart · {symbol} · chart feed is Cboe BZX real-time',
  'detail.news': 'News',
  'detail.newsSource': 'Massive · recent',
  'detail.noNews': 'No news.',
  'detail.history': 'History',
  'detail.historySource': 'Massive · daily bars',
  'detail.period1m': '1M',
  'detail.period3m': '3M',
  'detail.period6m': '6M',
  'detail.period1y': '1Y',
  'detail.sinceBuy': 'Since purchase',
  'detail.lockedTitle': 'News and history need a Massive Starter key',
  'detail.lockedFinnhub': 'You are on the free Finnhub key: real-time quotes and basic fundamentals are available.',
  'detail.lockedNoKey': 'No market-data key is configured.',
  'detail.lockedBody': 'Price history and company news come from Massive Starter ($29/month, paid by you).',
  'detail.lockedCta': 'Configure in Settings',
  'detail.fundamentals': 'Fundamentals',
  'detail.pe': 'P/E (TTM)',
  'detail.range52': '52-week range',
  'detail.beta': 'Beta',
  'detail.dividend': 'Dividend yield',
  'detail.mcap': 'Market cap',
  'detail.ecb': 'ECB {date}',

  'experts.title': 'Expert roundtable',
  'experts.new': 'New discussion',
  'experts.delete': 'Delete discussion',
  'experts.confirmDelete': 'Delete this discussion?',
  'experts.context': 'Holdings context sent to the experts',
  'experts.pickHint': 'Each style is a public school of investing. The styles you select analyse your holdings independently, then debate.',
  'experts.pickAria': 'Investment styles',
  'experts.info': 'Info',
  'experts.infoClose': 'Close',
  'experts.pick': 'Choose investment styles · {n} / {max}',
  'experts.fixed': 'Participants are fixed once a discussion starts; open a new one to change them.',
  'experts.disclaimer': 'The “experts” are AI style profiles built on public schools of investing. They represent no real person or firm, are unaffiliated with any, and are not investment advice.',
  'experts.references': 'Method references: ',
  'experts.referencesNote': 'Each style is distilled from methods set out in published works. It is unaffiliated with, and not endorsed by, the authors listed or their organizations; the AI-generated opinions do not represent their views.',
  'experts.presets': 'Preset questions',
  'experts.startTitle': 'Let a few experts look at your holdings',
  'experts.startBody': 'Pick participants on the left, then choose a preset question or type your own below. Each expert answers independently and in parallel; afterwards you can have them debate.',
  'experts.needKey': 'An LLM key is required first.',
  'experts.goSettings': 'Open Settings',
  'experts.askPlaceholder': 'Ask the experts…',
  'experts.round': 'Round {n} · {type}',
  'experts.answer': 'answers',
  'experts.debate': 'debate',
  'experts.parallel': ' · {n, plural, one {# expert} other {# experts in parallel}}',
  'experts.me': 'Me',
  'experts.withContext': '(holdings context attached)',
  'experts.startDebate': 'Start a debate',
  'experts.debateHint': 'Round closed. The experts will respond to each other’s opinions point by point.',
  'experts.needTwo': 'At least two experts must have answered in the last round',
  'experts.progress': '{done} / {total} experts done; finished answers are kept.',
  'experts.followUp': 'Follow up, or pick a preset question…',
  'experts.opinions': '{n, plural, one {# opinion} other {# opinions}}',
  'experts.generating': 'Generating',
  'experts.failed': 'Failed',
  'experts.hidden': '{n, plural, one {# opinion} other {# opinions}} below {pct}% confidence hidden',
  'experts.expand': 'Show full analysis ▾',
  'experts.collapse': 'Hide analysis ▴',
  'experts.all': 'All',
  'experts.expandAll': 'Expand all ▾',
  'experts.collapseAll': 'Collapse all ▴',
  'experts.noLLM': 'Configure an LLM key in Settings first.',
  'experts.untitled': 'Untitled discussion',
  'experts.contextIntro': '——\nMy holdings context (auto-generated; amounts expressed as weights):',

  'preset.concentration.label': 'Assess concentration and risk',
  'preset.concentration.text': 'Assess the concentration and main risks of my current holdings: which exposures are too large, which correlations are underestimated, whether the cash ratio makes sense. Be specific down to position percentages.',
  'preset.action.label': 'Add, trim, or hold?',
  'preset.action.text': 'Given my holdings and the current market environment, should I add, trim, or hold? For each relevant position give the direction, the size, and the trigger.',
  'preset.missing.label': 'What is this portfolio missing?',
  'preset.missing.text': 'What is this portfolio missing? Point out gaps across asset classes, risk factors, liquidity and time horizon, and say whether each is worth filling.',
  'preset.stress.label': 'Run a stress test',
  'preset.stress.text': 'Stress-test my portfolio: rates up another 50 bp, US equities down 15%, crypto down 40%. Estimate the impact of each and name the weakest link.',

  'ctx.total': 'Total assets about {amount} USD (at current quotes and ECB rates)',
  'ctx.classes': 'By class: stocks {stock} · ETFs {etf} · mutual funds {fund} · cash {cash} · crypto {crypto}',
  'ctx.day': 'Today: {pct} (positions with a known change)',
  'ctx.detail': 'Positions (by weight, descending):',
  'ctx.cash': '{ccy} cash',
  'ctx.weight': 'weight {pct}',
  'ctx.price': 'price {price} {ccy} (quoted {at})',
  'ctx.priceManual': 'price {price} USD (entered by hand)',
  'ctx.rate': 'rate 1 {ccy} = {usd} USD',
  'ctx.today': 'today {pct}',
  'ctx.open': 'open {price}',
  'ctx.range': 'low–high {low}–{high}',
  'ctx.range24h': '24h low–high {low}–{high}',
  'ctx.prevClose': 'previous close {price}',
  'ctx.prevNav': 'previous NAV {price}',
  'ctx.pnl': 'vs cost {pct}',
  'ctx.since': 'held since {date}',
  'ctx.note': 'note: {note}',
  'ctx.unpriced': '{n, plural, one {# more holding has} other {# more holdings have}} no price yet and {n, plural, one {is} other {are}} left out of the total and the weights:',
  'ctx.empty': '(no priced holdings yet)',
  'ctx.benchHead': 'Market benchmarks (from TMX Money, quote times in ET. "ETF proxy" = the day\'s change of the ETF tracking that index stands in for the index; the two usually differ by less than 0.1 percentage point; an ETF price is not an index level, so none is listed):',
  'ctx.benchProxy': '{label} (ETF proxy {proxy})',
  'ctx.benchLevel': 'level {level}',
  'ctx.benchAt': 'quoted {at}',
  'bench.spx': 'S&P 500',
  'bench.dji': 'Dow Jones Industrial Average',
  'bench.nasdaq': 'Nasdaq Composite',
  'bench.tsx': 'S&P/TSX Composite',


  'setup.title': 'First-time setup',
  'ds.colSource': 'Source',
  'ds.colCost': 'Cost',
  'ds.colGives': 'What it provides',
  'ds.colWhen': 'When you need it',
  'ds.builtin.name': 'Built in (no key)',
  'ds.builtin.cost': 'Free',
  'ds.builtin.gives': 'Quotes for US and Canadian stocks, ETFs, CDRs and Canadian mutual funds (TMX Money, about 15 minutes delayed); crypto (Coinbase); FX (ECB).',
  'ds.builtin.when': 'To see values, daily changes and the briefs: nothing to configure. US listings are added by exact ticker, not searched by name.',
  'ds.finnhub.name': 'Finnhub',
  'ds.finnhub.cost': 'Free key',
  'ds.finnhub.gives': 'Real-time US stock and ETF quotes; US search by company name; company profile and fundamentals.',
  'ds.finnhub.when': 'When you want real-time US prices, or to find a ticker by company name.',
  'ds.massive.name': 'Massive Starter',
  'ds.massive.cost': '$29 / month, self-paid',
  'ds.massive.gives': 'Five years of price history and company news on the detail page; US quotes (15 minutes delayed; Finnhub still wins when present).',
  'ds.massive.when': 'When you want charts of history and news on the detail page.',
  'ds.twelvedata.name': 'Twelve Data Grow',
  'ds.twelvedata.cost': '$29 / month, self-paid',
  'ds.twelvedata.gives': 'Real-time TSX / TSXV / CSE / Cboe Canada quotes, CDRs included.',
  'ds.twelvedata.when': 'When you want real-time Canadian prices; otherwise the built-in delayed quotes apply.',
  'ds.note': 'Keys stay in this browser and go straight to their provider; several can be configured side by side, each for its own job.',
  'setup.titleAgain': 'Setup wizard',
  'setup.welcomeTitle': 'Welcome to AssetList',
  'setup.welcomeLead': 'A portfolio viewer that lives in your own browser.',
  'setup.introWhatLabel': 'What it does',
  'setup.introWhat1': 'One page for the value and daily change of everything you hold — US and Canadian stocks, CDRs, ETFs, mutual funds, cash, crypto — in the currency you choose.',
  'setup.introWhat2': 'Pre-market and post-market briefs, a portfolio check-up and a stress test, generated with your own AI key — or copy the prompt into your own ChatGPT / Claude / Gemini.',
  'setup.introWhat3': 'Eight investment styles analyse your holdings independently, then debate each other.',
  'setup.introPrivacyLabel': 'Data and privacy',
  'setup.introPrivacy1': 'No server, no account. Holdings, costs, keys and snapshots stay in this browser and are never uploaded anywhere.',
  'setup.introPrivacy2': 'Quote and AI requests go from your browser straight to the providers of your own accounts; AssetList is not in the path. Every request can be checked in the browser’s network panel.',
  'setup.introPrivacy3': 'The context sent to an AI holds tickers, weights, daily changes, cost basis as percentages and an approximate total — no account, broker or identity details. The exact text is always visible on the Experts page.',
  'setup.introPrivacy4': 'Clearing site data clears everything — export a backup from Settings now and then.',
  'setup.introNextLabel': 'Next',
  'setup.introNext1': 'Three steps: an LLM key (optional), a market-data key (optional), a first portfolio. Every step can be skipped.',
  'setup.introNext2': 'Everything can be changed later in Settings, and this wizard can be run again at any time.',
  'setup.aboutLink': 'About AssetList: why it exists, how privacy is protected, disclaimer and terms of use →',
  'setup.begin': 'Start setup →',
  'setup.enterNow': 'Go in now, set up later',
  'settings.rerunSetup': 'Run the setup wizard again',
  'settings.rerunSetupHint': 'Re-introduces the tool and where data lives, then walks through keys and portfolios; existing settings are kept.',
  'settings.aboutLink': 'Privacy, disclaimer and terms of use',
  'setup.tagline': 'No server, no account — you hand your own keys to your own browser.',
  'setup.step1': 'LLM key',
  'setup.step2': 'Market-data key',
  'setup.step3': 'Done',
  'setup.verified': '· verified',
  'setup.llmTitle': 'Connect your LLM',
  'setup.llmLead': 'The expert roundtable and in-app reports run on the model you choose, billed to your own account. Optional: report templates can also be copied into your own ChatGPT / Claude / Gemini. Pick any of the three; you can switch later in Settings.',
  'setup.llmNotice': 'The key is stored only in this browser and sent straight to the provider’s official API. AssetList has no server and never sees it — check every request in your browser’s network panel.',
  'setup.later': 'Later',
  'setup.dataTitle': 'Choose a market-data plan',
  'setup.dataLead': 'Nothing here is required. Quotes are fetched directly from your own accounts and AssetList never touches them; the table shows what each source provides and when it is worth having.',
  'setup.dataPick': 'Only one decision now: do you want real-time US quotes? If so, get a free Finnhub key; everything else can be added later in Settings.',
  'setup.free': 'Free',
  'setup.finnhub1': 'Real-time US stock / ETF quotes, basic fundamentals, ticker search',
  'setup.finnhub2': '60 calls per minute — plenty for one person',
  'setup.finnhub3': 'No price history or company news',
  'setup.finnhubApply': 'Get a free key (about a minute) →',
  'setup.massivePrice': '$29 / month · paid by you',
  'setup.massive1': 'US stock / ETF quotes (15-min delayed), ticker search',
  'setup.massive2': '5 years of price history and company news, unlocking the history and news sections',
  'setup.massive3': 'Personal-use licence; the key is yours',
  'setup.massiveApply': 'Subscribe at massive.com →',
  'setup.dataNotice': 'This key is only ever sent to {host}. Clearing site data erases everything — remember to export a backup.',
  'setup.skipHint': 'Works without one: quotes come from the built-in source, about 15 minutes delayed',
  'setup.portfolioTitle': 'Create your first portfolio',
  'setup.portfolioLead': 'A portfolio is one account or one kind of asset; rename it any time. You add assets to it from the list.',
  'setup.portfolioName': 'Portfolio name',
  'setup.portfolioPlaceholder': 'e.g. IBKR brokerage',
  'setup.portfolioDefault': 'Brokerage account',
  'setup.portfolioHave': 'You already have {n, plural, one {# portfolio} other {# portfolios}}.',
  'setup.enter': 'Go to my assets →',

  'keys.llmKey': '{provider} API key',
  'keys.finnhubKey': 'Finnhub API key',
  'keys.massiveKey': 'Massive API key',
  'keys.massivePlaceholder': 'Paste your Massive API key',
  'keys.providerNote': 'The roundtable and the briefs all use this provider, billed to your account.',
  'keys.verify': 'Save & verify',
  'keys.verifying': 'Verifying…',
  'keys.verifiedModels': 'Verified · {n, plural, one {# model} other {# models}} available',
  'keys.verifyFailedFallback': 'Verification failed: {error}. An offline model list was loaded so you can pick a model.',
  'keys.verifiedFinnhub': 'Verified · real-time quotes · {sample}',
  'keys.verifiedMassive': 'Verified · 15-min delayed quotes · {sample}',
  'keys.verifyFailed': 'Verification failed: {error}',
  'keys.statusLive': 'Verified · {n, plural, one {# model} other {# models}} · {date}',
  'keys.statusFallback': 'Offline model list, not verified',
  'keys.statusOk': 'Verified · {detail}',
  'keys.statusInvalid': 'Invalid key: {detail}',
  'keys.statusUnverified': 'Not verified',
  'keys.statusNone': 'Not configured',
  'keys.clear': 'Clear key',
  'keys.model': 'Default model',
  'keys.modelPlaceholder': '— save & verify the key first —',
  'keys.modelNote': 'Side tasks such as titles use the provider’s light tier automatically',

  'settings.title': 'Settings',
  'settings.navLLM': 'LLM & model',
  'settings.navData': 'Market-data keys',
  'settings.navDisplay': 'Display',
  'settings.navPrivacy': 'Data & privacy',
  'settings.finnhubOk': 'Verified · real-time quotes',
  'settings.massiveOk': 'Verified · history & news',
  'settings.keyInvalid': 'Invalid key',
  'settings.keyNone': 'Not configured',
  'settings.finnhubMeta': 'Free · 60 calls/min · ',
  'settings.apply': 'Get a key →',
  'settings.massiveMeta': '$29/month, paid by you · 5-year history · company news · ',
  'settings.subscribe': 'Subscribe →',
  'settings.language': 'Language',
  'settings.languageAuto': 'Follow the browser ({detected})',
  'settings.currency': 'Display currency',
  'settings.fx': 'Exchange rates',
  'settings.fxText': 'ECB reference rates · updated every working day at 16:00 CET',
  'settings.fxCurrent': ' · current {date}',
  'settings.costNote': ' · cost is recorded in the purchase currency',
  'settings.refresh': 'Refresh',
  'settings.refreshText': 'Every 60 s during the US session (only while the page is visible); crypto around the clock; the close is shown and labelled while the market is closed',
  'settings.refreshNow': 'Refresh now',
  'settings.change': 'Change',
  'settings.changeText': 'Shows “today’s change”',
  'settings.changeNote': ' · P&L with dividends and splits comes in phase two',
  'settings.export': 'Export all data · JSON',
  'settings.import': 'Import backup',
  'settings.clear': 'Clear local data',
  'settings.stats': '{portfolios, plural, one {# portfolio} other {# portfolios}} · {assets, plural, one {# asset} other {# assets}} · {days, plural, one {# day} other {# days}} of snapshots · about {size}',
  'settings.promise1': 'AssetList has no server that stores user data. Holdings, costs and keys live only in this browser.',
  'settings.promise2': 'Your data goes only to the accounts you configured: the market-data provider and the LLM provider. The roundtable sends your holdings overview (as weights) to the LLM you chose.',
  'settings.promise3': 'Every request leaves directly from your browser; you can verify where each one goes in your browser’s network panel.',
  'settings.confirmClear': 'Clear all local data ({portfolios, plural, one {# portfolio} other {# portfolios}}, {assets, plural, one {# asset} other {# assets}}, {days, plural, one {# day} other {# days}} of snapshots)? This cannot be undone — export a backup first.',
  'settings.cleared': 'Local data cleared',
  'settings.importFailed': 'Import failed: {error}',

  'notice.exported': 'Backup exported (keys excluded)',
  'notice.imported': 'Imported {portfolios, plural, one {# portfolio} other {# portfolios}} and {assets, plural, one {# asset} other {# assets}}',
  'notice.notBackup': 'This is not an AssetList backup file',
  'notice.fxFailed': 'Could not fetch exchange rates: {error}',
  'notice.quotesFailed': 'Could not fetch stock quotes: {error}',
  'notice.priceFailed': 'Could not fetch a price for {symbol}: {error}',
  'notice.interrupted': 'Generation was interrupted by a page reload',

  'err.connect': 'Could not reach {label}',
  'err.invalidKey': '{label} key invalid or unauthorized ({status}){detail}',
  'err.rateLimited': '{label} rate limit hit (429)',
  'err.notFound': '{label} does not know this ticker',
  'err.upstream': '{label} returned {status}{detail}',
  'err.noQuote': '{label} has no quote for {symbol}',
  'err.noSnapshot': 'Massive returned no snapshot for {symbol}',

  'llm.aborted': 'Stopped',
  'llm.connect': 'Could not reach {host}',
  'llm.invalidKey': 'Key invalid or unauthorized ({status}){detail}',
  'llm.quota': 'Provider quota exhausted or no credit ({status}){detail}. Add credits with the provider or switch AI provider in Settings.',
  'llm.rateLimited': 'Provider rate limit (429){detail}',
  'llm.contextTooLong': 'Context exceeds the model limit (400){detail}',
  'llm.upstream': 'Provider returned {status}{detail}',
  'llm.streamError': 'Streaming error',
  'llm.streamInterrupted': 'Stream interrupted: {error}',
  'llm.badJson': 'The model did not return valid chatResponse JSON',
  'llm.blocked': 'Request blocked: {reason}',

  'run.busy': 'This discussion already has a round in progress',
  'run.noLLM': 'No LLM key or default model configured — open Settings first.',
  'run.unknownProfile': 'Unknown profile: {id}',
  'run.repair': 'Your previous output was not valid JSON. Re-emit the same opinions as a single JSON object that follows the contract, with nothing else.',
  'run.titleSystem': 'You title a discussion. Output only the title: at most 6 words, no quotes, no markdown, no trailing punctuation.',
  'run.titleUser': 'Question: {question}\n\nConclusions:\n{conclusions}',
  'run.debateReplay': '[DEBATE] Each expert responded point by point to the other experts’ opinions from the previous round (agree / disagree / add).',
  'run.debateInstruction': '[DEBATE] The experts have given their opinions in the previous round (above). You are {name}. Respond to each of the **other** experts’ main opinions one by one — agree, disagree or qualify — with reasons, giving a confidence for each response. Keep your own framework and hard constraints; never change position merely to agree.',
  'run.replayConfidence': '(confidence {pct}%)',
  'run.expertFallback': 'Expert',
  'list.featLabel': 'Built on these holdings · two optional features',
  'list.featOptional': 'Skip them and the ledger still works',
  'list.featReportsCount': '{n} templates',
  'list.featReportsBody': 'Pre-market brief, post-market recap, portfolio check-up, stress test. Generate here, or copy the prompt with your holdings filled in into your own ChatGPT / Claude.',
  'list.featExpertsCount': '{n} investment styles',
  'list.featExpertsBody': 'Have the AI assess these holdings from several investment styles, then debate. Needs your own LLM key.',
  'list.featLast': 'Latest: {what}',
  'list.featNone': 'Not used yet',
  'list.open': 'Open →',
  'reports.title': 'Reports',
  'reports.heading': 'Pick a template, use it either way',
  'reports.lead': 'Each template is a finished prompt. Generate it here with your API key, or copy it into your own ChatGPT, Claude or Gemini and run it on the subscription you already pay for, with no API charges. Your holdings are filled in as weights.',
  'reports.snapshot': 'Holdings snapshot {time} ET · {n, plural, one {# position} other {# positions}} · {total}',
  'reports.llmBadge': 'In-app: {model} · your key',
  'reports.noKeyBadge': 'No LLM key · you can still copy prompts',
  'reports.needsWeb': 'Needs web search',
  'reports.offline': 'From holdings',
  'reports.searches': '~{n} searches',
  'reports.estCost': 'In-app ~{usd} per run',
  'reports.copy': 'Copy prompt',
  'reports.run': 'Generate here',
  'reports.running': 'Generating…',
  'reports.waitClose': 'After the close',
  'reports.lastToday': 'Latest: today {time}',
  'reports.last': 'Latest: {date}',
  'reports.never': 'Not generated yet',
  'reports.view': 'View',
  'reports.waysTitle': 'Two ways to use a template',
  'reports.way1Title': 'Copy the prompt into your own account',
  'reports.way1Body': 'Open ChatGPT, Claude or Gemini on the web, paste, turn on web search, send. It runs on the subscription you already have; no API charges. The result does not come back here.',
  'reports.way2Title': 'Generate here with your API key',
  'reports.way2Body': 'Holdings and the current time are filled in automatically; the result is kept in this browser, exportable, and can go to the experts. Pay per use; the card shows an estimate.',
  'reports.privacy': 'Both ways send the same content: total assets, each position’s weight and current price, today’s change, gain versus cost. No quantities, cost amounts or account names.',
  'reports.recent': 'Recent · stored in this browser',
  'reports.noRecent': 'No reports yet.',
  'reports.scenarioPlaceholder': 'Scenario, e.g. “AAPL misses tomorrow, Nasdaq −3%”',
  'reports.needAssets': 'Add assets first',
  'tpl.premarket.name': 'Pre-market brief',
  'tpl.premarket.when': 'Before the open',
  'tpl.premarket.desc': 'Last 24 hours of news for every position; US inflation, jobs and the Fed; crypto and non-USD currency notes; a watch list for today. Every item cites its source.',
  'tpl.postmarket.name': 'Post-market recap',
  'tpl.postmarket.when': 'After 16:00 ET',
  'tpl.postmarket.desc': 'How the three indexes closed and why; a table of which positions beat or lagged the market; each position’s open, close and attribution; what to watch next session. On a holiday, a digest of the day’s news instead.',
  'tpl.checkup.name': 'Portfolio check-up',
  'tpl.checkup.when': 'Any time',
  'tpl.checkup.desc': 'Reviews the current holdings for concentration, correlation, cash and liquidity, points out what is missing, and suggests a weight range for each position.',
  'tpl.stress.name': 'Stress test',
  'tpl.stress.when': 'Any time',
  'tpl.stress.desc': 'Set a scenario (e.g. Nasdaq −5% in a day); get its impact on each position and on the total, and the order in which to respond.',
  'tpl.stress.defaultScenario': 'US market −5% in a day, Nasdaq −7%',
  'copy.title': 'Copy prompt',
  'copy.language': 'Output language',
  'copy.hint.web': 'This report needs the web. Before pasting into ChatGPT, Claude, Gemini or any other chat product, make sure web search is on (Search in ChatGPT, web search in Claude; Gemini has it by default). Paste and send as is; if the answer lacks source links, ask it to add a link to every item.',
  'copy.hint.offline': 'Paste and send as is.',
  'copy.preview': 'What will be copied · today’s holdings filled in',
  'copy.size': '~{n} chars · snapshot {time} ET',
  'copy.privacy': 'Includes total assets, weights and current prices, today’s change, gain versus cost. No quantities, cost amounts or account names.',
  'copy.instead': 'Generate here instead',
  'copy.download': 'Download .md',
  'copy.all': 'Copy all',
  'copy.copied': 'Copied',
  'report.regenerate': 'Regenerate',
  'report.export': 'Export · Markdown',
  'report.askExperts': 'Take it to the experts',
  'report.meta': 'Generated in-app {time} ET · {model}',
  'report.metaSearches': '{n, plural, one {# search} other {# searches}}',
  'report.today': 'Generated today · opening does not repeat it',
  'report.inputs': 'Inputs · all from this browser',
  'report.snapshot': 'Holdings snapshot',
  'report.total': 'Total assets',
  'report.positions': 'Positions',
  'report.fxDate': 'FX date',
  'report.scenario': 'Scenario',
  'report.queries': 'Searches · {n}',
  'report.cost': 'This run',
  'report.costSearches': '{n} searches',
  'report.costTokens': 'Input {i} · output {o} tokens',
  'report.costTotal': 'Total',
  'report.costNoteFeed': 'From the usage the provider returned, not an estimate; unit prices from LiteLLM’s public price table (updated {date}).',
  'report.costNoteApprox': 'From the usage the provider returned; {model} has no official row in the price table, so the unit price is approximated from a reseller’s row of the same model (LiteLLM table, {date}).',
  'report.costNoteBuiltin': 'From the usage the provider returned, not an estimate; the online price table is unavailable, so unit prices come from the built-in table ({date}).',
  'report.costUnknown': 'Model not in the price table; usage only. The table refreshes daily and new models usually appear within days; the cost fills in here once it does.',
  'report.disclaimer': 'Generated by the LLM you configured. Information only, not investment advice. Sources are live search results at generation time.',
  'report.sourcesHeading': 'Sources',
  'report.failed': '{name} failed: {error}',
  'report.noAssets': 'No holdings yet — add assets first (watch items are not reported on).',
  'setup.optional': '· optional',

  'market.US': 'United States',
  'market.CA': 'Canada',
  'class.stockCA': 'Canadian stock',
  'class.etfCA': 'Canadian ETF',
  'class.cdr': 'CDR',
  'ctx.cdr': 'CDR: CAD-denominated, currency-hedged depositary receipt of the US share',
  'add.market': 'Market',
  'add.cdrHint': 'CDR: a CAD-denominated, currency-hedged depositary receipt of the US share, traded on Cboe Canada and the TSX. The quote is the CDR itself, not the US share price.',
  'keys.twelvedataKey': 'Twelve Data API key',
  'keys.twelvedataPlaceholder': 'Paste your Twelve Data API key',
  'keys.verifiedTwelvedata': 'Verified · Canadian quotes available · {sample}',
  'settings.twelvedataOk': 'Verified · Canadian quotes',
  'settings.twelvedataMeta': 'Grow, $29/mo self-paid · real-time TSX / TSXV / CSE / Cboe Canada quotes incl. CDRs; without it CAD quotes come from TMX Money (free, 15-minute delay) · ',
  'class.fund': 'Mutual fund',
  'class.fundCA': 'Canadian mutual fund',
  'ctx.fund': 'mutual fund (bought off-exchange, FundSERV code, not listed); the price is the daily published NAV',
  'add.fundCode': 'Fund code (FundSERV)',
  'add.fundName': 'Fund name',
  'add.fundNamePlaceholder': 'e.g. Fidelity Global Innovators Class F',
  'add.units': 'Units',
  'add.nav': 'NAV · {ccy}',
  'add.navNote': 'Canadian mutual funds get their NAV from TMX Money by FundSERV code (updated each trading evening); the NAV entered here is used only when that lookup fails.',
  'add.tmxFund': 'TMX Money: {name} · NAV {nav} {ccy} ({date})',
  'add.tmxFundMiss': 'TMX Money has no code {code}; check it, or enter the NAV by hand.',
  'add.navLookup': 'Look up {code} →',
  'add.classHint.etf': 'ETFs trade on an exchange under a ticker and have intraday quotes. A fund bought from the fund company or a bank, with only a daily NAV, goes under “Mutual fund”.',
  'add.classHint.fund': 'Mutual funds are bought from the fund company or a bank, have no ticker, are identified by FundSERV code and publish one NAV a day. An exchange-traded fund goes under “ETF”.',
  'detail.openGoogleFund': 'NAV on Google Finance',
  'detail.updateNav': 'Enter NAV by hand',
  'detail.refreshNav': 'Refresh NAV',
  'detail.refreshingNav': 'Refreshing…',
  'detail.navRefreshed': '{symbol} NAV {nav} {ccy} ({date})',
  'detail.navNotFound': 'TMX Money has no NAV for {symbol}; use “Look up NAV online” below or enter it by hand.',
  'detail.units': 'units',
  'detail.fundNote': 'Mutual fund, NAV maintained by hand, last updated {date}. No chart.',
  'detail.fundTmx': 'Mutual fund; NAV from TMX Money, published {date}, refreshed automatically each trading evening. No chart.',
  'detail.fundNoDate': 'Mutual fund, NAV maintained by hand. No chart.',
  'fresh.manualDated': 'Manual · {date}',
  'nav.lookup': 'Look up NAV online',
  'nav.looking': 'Looking up…',
  'nav.found': 'Found {nav} {ccy} · {date} · {source}{cost}',
  'nav.apply': 'Apply',
  'nav.applied': 'NAV updated to {nav} {ccy} ({date})',
  'nav.check': 'Currency differs or the date is over 10 days old; check before applying.',
  'nav.needLLM': 'Configure an LLM key first',
  'nav.unparsable': 'The model did not return a readable NAV',
  'nav.notFound': 'No reliable NAV found: {reason}',
  'nav.rejected': 'Found {nav} {ccy} ({date}) but currency differs or the date is stale; not applied',
  // Watch items (assets with quantity 0)
  'add.watchHint': 'Quantity 0 adds a watch item: its price is tracked, but it adds nothing to the total and stays out of reports and the expert discussion. Enter the real quantity once you buy.',
  'add.watchToHolding': 'This is a watch item. Enter the quantity you hold and it becomes a holding: counted in the total, covered by reports and the experts.',
  'add.previewWatch': 'Watch item — no value',
  'add.submitWatch': 'Add to watch',
  'list.watch': 'Watch',
  'detail.watching': 'Watching',
  'detail.watchNote': 'Watch item: its price is tracked, but it adds nothing to the total and stays out of reports and the expert discussion. To make it a holding, edit it and enter a quantity.',
};

export default en;
